import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, User, CreditCard, LogOut, Menu,
  TrendingUp, AlertTriangle, Clock, MapPin, ChevronRight, MessageCircle, ShoppingBag, ShoppingCart, Users, BarChart3, Timer, DollarSign, XCircle, Receipt, Building2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import NotificationBell from "../../components/NotificationBell";
import SubscriptionExpiryBanner from "../../components/SubscriptionExpiryBanner";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { Trans } from "react-i18next";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

// Sidebar Navigation
function navItemActive(pathname, itemPath) {
  if (itemPath === "/seller") {
    return pathname === "/seller" || pathname.startsWith("/seller/");
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

const Sidebar = ({ className = "", onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation("nav");
  const { t: tc } = useTranslation("common");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingPurchases, setPendingPurchases] = useState(0);
  const [pendingCancellations, setPendingCancellations] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const convResponse = await apiClient.get("/messages/conversations");
      const unread = convResponse.data.reduce((sum, conv) => sum + (conv.unread_pharmacy || 0), 0);
      setUnreadMessages(unread);

      const purchasesResponse = await apiClient.get("/purchases/pharmacy");
      const pending = purchasesResponse.data.filter(p => p.status === "pending").length;
      setPendingPurchases(pending);
      
      // Fetch pending cancellation requests
      try {
        const cancelResponse = await apiClient.get("/pos/cancellation-requests");
        setPendingCancellations(cancelResponse.data?.length || 0);
      } catch (e) {
        console.log("No cancellation requests");
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const navItems = [
    { icon: LayoutDashboard, labelKey: "pharmacy.dashboard", path: "/dashboard" },
    { icon: Package, labelKey: "pharmacy.inventory", path: "/dashboard/inventory" },
    { icon: ShoppingCart, labelKey: "pharmacy.pos", path: "/seller" },
    { icon: Receipt, labelKey: "pharmacy.salesHistory", path: "/seller/sales", badge: pendingCancellations },
    { icon: Users, labelKey: "pharmacy.sellers", path: "/dashboard/sellers" },
    { icon: BarChart3, labelKey: "pharmacy.marketData", path: "/dashboard/market-data" },
    { icon: Timer, labelKey: "pharmacy.expiring", path: "/dashboard/expiring-products" },
    { icon: DollarSign, labelKey: "pharmacy.stockValue", path: "/dashboard/stock-value" },
    { icon: Building2, labelKey: "pharmacy.business", path: "/dashboard/business" },
    { icon: MessageCircle, labelKey: "pharmacy.messages", path: "/messages", badge: unreadMessages },
    { icon: ShoppingBag, labelKey: "pharmacy.purchases", path: "/purchases", badge: pendingPurchases },
    { icon: User, labelKey: "pharmacy.profile", path: "/dashboard/profile" },
    { icon: CreditCard, labelKey: "pharmacy.subscription", path: "/dashboard/subscription" },
  ];

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      <div className="p-6 flex-shrink-0">
        <Link to="/">
          <img src={LOGO_URL} alt="OMU" className="h-16 w-auto" />
        </Link>
      </div>

      <nav className="flex-1 px-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              navItemActive(location.pathname, item.path)
                ? "bg-omu-blue-500 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`nav-${item.labelKey.split(".").pop()}`}
          >
            <item.icon className="h-5 w-5" />
            <span className="font-medium flex-1">{t(item.labelKey)}</span>
            {item.badge > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                navItemActive(location.pathname, item.path)
                  ? "bg-white text-omu-blue-500"
                  : "bg-red-500 text-white"
              }`}>
                {item.badge}
              </span>
            )}
          </Link>
        ))}
      </nav>

      <div className="p-4 flex-shrink-0 border-t border-slate-100 space-y-3">
        <LanguageSwitcher className="w-full justify-center" compact />
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-600 hover:text-red-500"
          onClick={() => {
            logout();
            navigate("/");
          }}
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5 mr-3" />
          {tc("logout")}
        </Button>
      </div>
    </div>
  );
};

// Dashboard Layout
export const DashboardLayout = ({ children, title }) => {
  const { t: tp } = useTranslation("pharmacy");
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isSubPage = location.pathname !== "/dashboard";

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col bg-white border-r border-slate-200">
        <Sidebar />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-2">
            {isSubPage && (
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-btn">
                <ChevronRight className="h-5 w-5 rotate-180" />
              </Button>
            )}
            <Link to="/">
              <img src={LOGO_URL} alt="OMU" className="h-10 w-auto" />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <Sidebar onNavigate={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Desktop Top Bar with Notifications */}
      <div className="hidden lg:block lg:fixed lg:top-0 lg:left-64 lg:right-0 lg:h-16 lg:bg-white lg:border-b lg:border-slate-200 lg:z-40">
        <div className="flex items-center justify-end h-full px-6">
          <NotificationBell />
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:pl-64 lg:pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-12">
          {/* Status Banner */}
          {user?.status === "pending" && !user?.subscription_active && (
            <Alert className="mb-6 bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                {tp("dashboard.pendingAccount")}
              </AlertDescription>
            </Alert>
          )}

          {user?.role === "pharmacy" && user?.status !== "pending" && (
            <SubscriptionExpiryBanner />
          )}

          {title && (
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">
              {title}
            </h1>
          )}

          {children}
        </div>
      </main>
    </div>
  );
};

// Main Dashboard Page
const PharmacyDashboard = () => {
  const { t: tp } = useTranslation("pharmacy");
  const { t: tn } = useTranslation("nav");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStock: 0,
    expiringSoon: 0
  });
  const [pharmacy, setPharmacy] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [pendingPurchases, setPendingPurchases] = useState([]);
  const [pendingCancellations, setPendingCancellations] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    
    // Polling pour les notifications toutes les 15 secondes
    const notificationInterval = setInterval(() => {
      fetchNotifications();
    }, 15000);
    
    return () => clearInterval(notificationInterval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const convRes = await apiClient.get("/messages/conversations");
      const unread = convRes.data.reduce((sum, conv) => sum + (conv.unread_pharmacy || 0), 0);
      setUnreadMessages(unread);
      
      try {
        const purchasesRes = await apiClient.get("/purchases/pharmacy");
        setPendingPurchases(purchasesRes.data.filter(p => p.status === "pending"));
      } catch (e) {}
      
      // Fetch pending cancellation requests
      try {
        const cancelRes = await apiClient.get("/pos/cancellation-requests");
        setPendingCancellations(cancelRes.data || []);
      } catch (e) {}
    } catch (e) {
      console.log("Notification fetch error");
    }
  };

  const fetchDashboardData = async () => {
    try {
      const profileRes = await apiClient.get("/pharmacy/profile");
      setPharmacy(profileRes.data);

      const inventoryRes = await apiClient.get("/pharmacy/inventory");
      const inventory = inventoryRes.data;

      setStats({
        totalProducts: inventory.length,
        lowStock: inventory.filter((i) => i.quantity <= i.alert_threshold).length,
        expiringSoon: inventory.filter((i) => {
          if (!i.expiry_date) return false;
          const expiry = new Date(i.expiry_date);
          const threeMonths = new Date();
          threeMonths.setMonth(threeMonths.getMonth() + 3);
          return expiry <= threeMonths;
        }).length
      });

      const subRes = await apiClient.get("/pharmacy/subscription");
      setSubscription(subRes.data.subscription);

      try {
        const purchasesRes = await apiClient.get("/purchases/pharmacy");
        setPendingPurchases(purchasesRes.data.filter(p => p.status === "pending"));
      } catch (e) {
        console.log("No purchases endpoint available");
      }

      try {
        const convRes = await apiClient.get("/messages/conversations");
        const unread = convRes.data.reduce((sum, conv) => sum + (conv.unread_pharmacy || 0), 0);
        setUnreadMessages(unread);
      } catch (e) {
        console.log("No messages endpoint available");
      }
      
      // Fetch pending cancellation requests
      try {
        const cancelRes = await apiClient.get("/pos/cancellation-requests");
        setPendingCancellations(cancelRes.data || []);
      } catch (e) {
        console.log("No cancellation requests");
      }

    } catch (error) {
      console.error("Dashboard data error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={tn("pharmacy.dashboard")}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-omu-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={tn("pharmacy.dashboard")}>
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-xl text-slate-600">
          {tp("dashboard.welcome")} <span className="font-semibold text-slate-900">{pharmacy?.entity_name}</span>
        </h2>
        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
          <MapPin className="h-4 w-4" />
          {pharmacy?.address}, {pharmacy?.city}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Products */}
        <Card className="border-0 shadow-soft stat-card" data-testid="stat-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{tp("dashboard.productsInStock")}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {stats.totalProducts}
                </p>
              </div>
              <div className="w-12 h-12 bg-omu-blue-50 rounded-xl flex items-center justify-center">
                <Package className="h-6 w-6 text-omu-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Low Stock */}
        <Card className="border-0 shadow-soft stat-card" data-testid="stat-low-stock">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{tp("dashboard.lowStock")}</p>
                <p className="text-3xl font-bold text-amber-500 mt-1">
                  {stats.lowStock}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expiring Soon */}
        <Card className="border-0 shadow-soft stat-card" data-testid="stat-expiring">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{tp("dashboard.expiringSoon")}</p>
                <p className="text-3xl font-bold text-red-500 mt-1">
                  {stats.expiringSoon}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                <Clock className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Status */}
        <Card className="border-0 shadow-soft stat-card" data-testid="stat-subscription">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{tp("dashboard.subscription")}</p>
                <p className="text-lg font-bold text-slate-900 mt-1">
                  {subscription ? subscription.plan_name : tp("status.none")}
                </p>
                <Badge
                  className={`mt-1 ${
                    subscription?.status === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {subscription?.status === "active" ? tp("status.active") : tp("status.inactive")}
                </Badge>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Purchases Alert */}
      {pendingPurchases.length > 0 && (
        <Alert className="mb-6 border-green-200 bg-green-50">
          <ShoppingBag className="h-5 w-5 text-green-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-green-800">
              <Trans ns="pharmacy" i18nKey="dashboard.pendingPurchases" values={{ count: pendingPurchases.length }} components={{ strong: <strong /> }} />
            </span>
            <Button 
              size="sm" 
              className="bg-green-600 hover:bg-green-700 rounded-full"
              onClick={() => navigate("/purchases")}
            >
              {tp("actions.viewRequests")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Pending Cancellation Requests Alert */}
      {pendingCancellations.length > 0 && (
        <Alert className="mb-6 border-amber-200 bg-amber-50" data-testid="cancellation-alert">
          <XCircle className="h-5 w-5 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-amber-800">
              <Trans ns="pharmacy" i18nKey="dashboard.pendingCancellations" values={{ count: pendingCancellations.length }} components={{ strong: <strong /> }} />
            </span>
            <Button 
              size="sm" 
              className="bg-amber-600 hover:bg-amber-700 rounded-full"
              onClick={() => navigate("/seller/sales")}
            >
              {tp("actions.manageCancellations")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Unread Messages Alert */}
      {unreadMessages > 0 && (
        <Alert className="mb-6 border-omu-blue-200 bg-omu-blue-50">
          <MessageCircle className="h-5 w-5 text-omu-blue-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-omu-blue-800">
              <Trans ns="pharmacy" i18nKey="dashboard.unreadMessages" values={{ count: unreadMessages }} components={{ strong: <strong /> }} />
            </span>
            <Button 
              size="sm" 
              className="bg-omu-blue-500 hover:bg-omu-blue-600 rounded-full"
              onClick={() => navigate("/messages")}
            >
              {tp("actions.viewMessages")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Inventory Card */}
        <Card
          className="border-0 shadow-soft cursor-pointer card-hover"
          onClick={() => navigate("/dashboard/inventory")}
          data-testid="quick-inventory"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  {tp("dashboard.manageInventory")}
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  {tp("dashboard.manageInventoryDesc")}
                </p>
              </div>
              <ChevronRight className="h-6 w-6 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        {/* Profile Card */}
        <Card
          className="border-0 shadow-soft cursor-pointer card-hover"
          onClick={() => navigate("/dashboard/profile")}
          data-testid="quick-profile"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  {tp("dashboard.editProfile")}
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  {tp("dashboard.editProfileDesc")}
                </p>
              </div>
              <ChevronRight className="h-6 w-6 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscription CTA */}
      {!subscription && user?.status === "active" && (
        <Card className="mt-8 border-0 bg-gradient-to-r from-omu-blue-500 to-omu-blue-600 text-white" data-testid="subscription-cta">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-xl font-semibold">
                  {tp("dashboard.activateSubscription")}
                </h3>
                <p className="opacity-90 mt-1">
                  {tp("dashboard.activateSubscriptionDesc")}
                </p>
              </div>
              <Button
                className="bg-white text-omu-blue-500 hover:bg-slate-100 rounded-full"
                onClick={() => navigate("/dashboard/subscription")}
              >
                {tp("actions.seePlans")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default PharmacyDashboard;
